import { SliderInput } from './slider-input';
import { LabelsType, ProductType, SavingsCalculatorStateType } from '../types';
import { FormSlider } from './slider';
import { Field } from '@/isomorphic/components/forms/components/field';
import { useLocalization } from '@/hooks/use-localization';
import { formatInterestRate } from '../hooks/use-calculate';
import { Products } from './products';
import { getTailwindColor } from '@/utils/colors';
import clsx from 'clsx';
import { useState } from 'react';

export type SavingsGrowthGraphCalculatorFormProps = {
    settings: {
        defaultMonthlyAmount: number;
        defaultStartAmount: number;
        defaultYears: number;
        maxMonthlyAmount: number;
        maxStartAmount: number;
        maxYears: number;
        minMonthlyAmount: number;
        minStartAmount: number;
        minYears: number;
    };
    CTAbutton: {
        title: string;
        url: string;
        rel?: string;
        target?: string;
        appendProductCode?: boolean;
    };
    chosenProduct: ProductType | null;
    setChosenProduct: (product: ProductType | null) => void;
    savingsCalculatorState: SavingsCalculatorStateType;
    showCustomInterestRateTextField?: boolean;
    products: ProductType[];
    updateSavingsCalculatorState: (state: Partial<SavingsCalculatorStateType>) => void;
    formatNumber: (number: number) => string;
    getLabel: (key: keyof LabelsType, replacements?: Record<string, string | number>) => string;
};

export const CalculatorForm = ({
    settings,
    savingsCalculatorState,
    updateSavingsCalculatorState,
    formatNumber,
    getLabel,
    products,
    chosenProduct,
    setChosenProduct,
    showCustomInterestRateTextField = false,
}: SavingsGrowthGraphCalculatorFormProps) => {
    const [isCustomInterestRateActive, setIsCustomInterestRateActive] = useState(false);
    const { localize } = useLocalization();

    const getRangeDescription = (min: number, max: number) => {
        return localize('input-range-description').replace('{min}', formatNumber(min)).replace('{max}', formatNumber(max));
    };

    return (
        <div className="r-mb-2">
            <div className="r-relative r-w-full r-space-y-8 r-px-2 md:r-max-w-xl md:r-px-0">
                {showCustomInterestRateTextField && (
                    <div className="r-mb-8 max-md:r--mx-4 max-md:r-px-2">
                        <div className="r-space-y-2 md:r-space-y-4">
                            {products?.length ? <h4 className="r-font-medium">{getLabel('customInterestRateHeader')}</h4> : null}
                            <Field
                                suffix="%"
                                label={getLabel('customInterestRate')}
                                value={`${savingsCalculatorState.customInterestRate}`}
                                isActive={isCustomInterestRateActive}
                                onChange={value => {
                                    setChosenProduct(null);
                                    const formatted = formatInterestRate(value);
                                    if (!isNaN(formatted)) {
                                        if (formatted > 100) {
                                            value = '100';
                                        }
                                        if (formatted < 0) {
                                            value = '0';
                                        }

                                        updateSavingsCalculatorState({ customInterestRate: value });
                                    }
                                    updateSavingsCalculatorState({ customInterestRate: value });
                                    setIsCustomInterestRateActive(true);
                                }}
                            />
                            {savingsCalculatorState.customInterestRate && isNaN(formatInterestRate(savingsCalculatorState.customInterestRate)) && (
                                <div className="r-border-l-4 r-border-l-danger r-bg-gray-100 r-p-4">{getLabel('invalidInterestRate')}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {!showCustomInterestRateTextField && products?.length === 1 ? (
                <div className="r-mb-2 r-flex r-flex-wrap r-items-center r-gap-x-2 r-gap-y-1 r-text-neutral-700">
                    <span className="r-font-bold">{products[0].name}</span>
                    <span>•</span>
                    <span>
                        {products[0].interest.type === 'FIXED' ? getLabel('fixed') : getLabel('variable')} {getLabel('rate')}:
                    </span>
                    <span className={clsx('r-rounded-full r-px-2 r-py-[2px] r-font-bold', getTailwindColor('accent8', 'bg'))}>
                        {products[0].interest.rate}%
                    </span>
                </div>
            ) : (
                <div className="r--mx-4 r-space-y-2 r-overflow-x-auto r-px-4 md:r-mx-0 md:r-space-y-4 md:r-overflow-x-visible md:r-px-0">
                    <h4 className="r-font-medium">{getLabel(showCustomInterestRateTextField ? 'orSelectProduct' : 'selectProduct')}</h4>
                    <Products
                        products={products}
                        getLabel={getLabel}
                        onClick={p => {
                            setIsCustomInterestRateActive(false);
                            setChosenProduct(p);
                        }}
                        chosenProduct={chosenProduct}
                    />
                </div>
            )}
            <div className="r-relative r-w-full r-space-y-8 r-px-2 r-pb-6 r-pt-8 md:r-mt-6 md:r-max-w-xl md:r-px-0">
                <fieldset className="r-m-0 r-flex r-flex-col r-border-none r-p-0">
                    <legend className="r-sr-only">
                        {getLabel('startAmount')} - {localize('input-slider-group-description')}
                    </legend>
                    <SliderInput
                        label={getLabel('startAmount')}
                        suffix={getLabel('currency')}
                        min={settings.minStartAmount}
                        max={settings.maxStartAmount}
                        value={savingsCalculatorState.startAmount}
                        onChange={value => updateSavingsCalculatorState({ startAmount: value })}
                        formatNumber={formatNumber}
                        rangeDescription={getRangeDescription(settings.minStartAmount, settings.maxStartAmount)}
                    />
                    <FormSlider
                        label={getLabel('startAmount')}
                        suffix={getLabel('currency')}
                        min={settings.minStartAmount}
                        max={settings.maxStartAmount}
                        step={1000}
                        value={savingsCalculatorState.startAmount}
                        onChange={value => updateSavingsCalculatorState({ startAmount: value })}
                        formatNumber={formatNumber}
                    />
                    <div className="r-my-4 r-flex r-w-full r-justify-between r-text-neutral-500" aria-hidden="true">
                        <span>
                            {formatNumber(settings.minStartAmount)} {getLabel('currency')}
                        </span>
                        <span>
                            {formatNumber(settings.maxStartAmount)} {getLabel('currency')}
                        </span>
                    </div>
                </fieldset>
                <fieldset className="r-m-0 r-flex r-flex-col r-border-none r-p-0">
                    <legend className="r-sr-only">
                        {getLabel('monthlyDeposit')} - {localize('input-slider-group-description')}
                    </legend>
                    <SliderInput
                        label={getLabel('monthlyDeposit')}
                        suffix={getLabel('currency')}
                        min={settings.minMonthlyAmount}
                        max={settings.maxMonthlyAmount}
                        value={savingsCalculatorState.monthlyDeposit}
                        onChange={value => updateSavingsCalculatorState({ monthlyDeposit: value })}
                        formatNumber={formatNumber}
                        rangeDescription={getRangeDescription(settings.minMonthlyAmount, settings.maxMonthlyAmount)}
                    />
                    <FormSlider
                        label={getLabel('monthlyDeposit')}
                        suffix={getLabel('currency')}
                        min={settings.minMonthlyAmount}
                        max={settings.maxMonthlyAmount}
                        step={500}
                        value={savingsCalculatorState.monthlyDeposit}
                        onChange={value => updateSavingsCalculatorState({ monthlyDeposit: value })}
                        formatNumber={formatNumber}
                    />
                    <div className="r-my-4 r-flex r-w-full r-justify-between r-text-neutral-500" aria-hidden="true">
                        <span>
                            {formatNumber(settings.minMonthlyAmount)} {getLabel('currency')}
                        </span>
                        <span>
                            {formatNumber(settings.maxMonthlyAmount)} {getLabel('currency')}
                        </span>
                    </div>
                </fieldset>
                <fieldset className="r-m-0 r-flex r-flex-col r-border-none r-p-0">
                    <legend className="r-sr-only">
                        {getLabel('savingsPeriod')} - {localize('input-slider-group-description')}
                    </legend>
                    <SliderInput
                        label={getLabel('savingsPeriod')}
                        suffix={
                            typeof savingsCalculatorState.years === 'number' && savingsCalculatorState.years > 1
                                ? getLabel('years')
                                : getLabel('year')
                        }
                        min={settings.minYears}
                        max={settings.maxYears}
                        value={savingsCalculatorState.years}
                        onChange={value => updateSavingsCalculatorState({ years: value })}
                        formatNumber={formatNumber}
                        rangeDescription={getRangeDescription(settings.minYears, settings.maxYears)}
                    />
                    <FormSlider
                        label={getLabel('savingsPeriod')}
                        suffix={
                            typeof savingsCalculatorState.years === 'number' && savingsCalculatorState.years > 1
                                ? getLabel('years')
                                : getLabel('year')
                        }
                        min={settings.minYears}
                        max={settings.maxYears}
                        value={savingsCalculatorState.years}
                        onChange={value => updateSavingsCalculatorState({ years: value })}
                        formatNumber={formatNumber}
                    />
                    <div className="r-mt-4 r-flex r-w-full r-justify-between r-text-neutral-500" aria-hidden="true">
                        <span>
                            {settings.minYears + ' '}
                            {typeof savingsCalculatorState.years === 'number' && savingsCalculatorState.years > 1
                                ? getLabel('years')
                                : getLabel('year')}
                        </span>
                        <span>
                            {settings.maxYears + ' '}
                            {typeof savingsCalculatorState.years === 'number' && savingsCalculatorState.years > 1
                                ? getLabel('years')
                                : getLabel('year')}
                        </span>
                    </div>
                </fieldset>
            </div>
        </div>
    );
};
